import React from 'react';
import { IDisbursementFormData } from 'types/order/escrow/IDisbursementFormData';
import { useDisbursementTypes } from '../../edit/disbursements/useDisbursementTypes';
import { ButtonDropdown } from 'ui-toolkit';
import { clientUrls, FeeTabs } from 'ui-toolkit/rest/ClientUrlFactory';
import { useHistory, useParams } from 'react-router';
import { UUID } from '../../../../../types/shared/UUID';
import { calculateAmountCentsToDisburse } from '../../edit/disbursements/calculateAmountCentsToDisburse';

interface IFeeDataRequired {
  stringFeePropertyValues: { [key: string]: string };
  numericFeePropertyValues: { [key: string]: number };
}

export interface IDisbursementStatusPillProps {
  disbursements: IDisbursementFormData[];
  onlyThisDisbursement?: IDisbursementFormData;
  isFeeDisplay?: boolean;
  hasQuickAdd?: boolean;
  feeId?: string;
  fee?: IFeeDataRequired;
  statementFeeId?: string;
}

export const DisbursementStatusPill: React.FC<IDisbursementStatusPillProps> = ({
  disbursements,
  onlyThisDisbursement,
  isFeeDisplay,
  hasQuickAdd,
  feeId,
  fee,
  statementFeeId,
}) => {
  const params = useParams<{ orderId: UUID; section: FeeTabs }>();
  const history = useHistory();

  const disbursementTypes = useDisbursementTypes();
  const disbursementOptions = disbursementTypes.filter((t) => t.hasEditPermission);
  const newDisbursementOptions = disbursementOptions.map((t) => ({
    children: t.type,
    onClick: () =>
      history.push(
        clientUrls.urlFor.feesTabEditWithNewLineItem(params.orderId, params.section, feeId!, statementFeeId, t.type)
      ),
  }));
  const amountCentsToDisburse = calculateAmountCentsToDisburse(
    fee?.numericFeePropertyValues,
    fee?.stringFeePropertyValues
  );
  const amountCentsPlanned = disbursements
    .filter((t) => !t.voidedAt)
    .reduce((acc, curr) => acc + (curr.amountCents ?? 0), 0);

  const amountMismatch = amountCentsToDisburse !== amountCentsPlanned;

  const timeToShowIncomplete =
    amountCentsToDisburse > 0 &&
    !!isFeeDisplay &&
    disbursements.length === 0 &&
    !!disbursementTypes.find((t) => t.hasReadPermission);

  const disbursementsToShow = onlyThisDisbursement ? [onlyThisDisbursement] : disbursements;
  return (
    <>
      {disbursementsToShow.map((t) => (
        <React.Fragment key={t.id}>
          {disbursementTypes.map((type) => (
            <React.Fragment key={type.type + t.id}>
              {type.type === t.type && type.hasReadPermission && (
                <type.statusPill disbursement={t} amountMismatch={amountMismatch} />
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
      {timeToShowIncomplete && (
        <span
          data-test-id={`status-pill`}
          className={`h-fit w-fit whitespace-nowrap rounded-full bg-gray-100 px-2 py-1 align-middle text-xs font-medium text-gray-800`}
        >
          {hasQuickAdd ? (
            <ButtonDropdown
              isInline
              intent={'ghost'}
              items={newDisbursementOptions}
              data-test-id={'add-new-disbursement'}
            >
              Incomplete
            </ButtonDropdown>
          ) : (
            'Incomplete'
          )}
        </span>
      )}
    </>
  );
};
